import React, { useEffect, useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { careerStories } from '../data/careerStories';

gsap.registerPlugin(ScrollTrigger);

const { FiArrowUpRight, FiLayers, FiTrendingUp, FiUsers } = FiIcons;

const icons = [FiTrendingUp, FiUsers, FiLayers];
const projects = careerStories.map((story, index) => ({
  ...story,
  id: index + 1,
  title: story.company,
  subtitle: story.role,
  description: story.summary,
  tags: story.focus.slice(0, 3),
  evidence: story.outcomes.slice(0, 2),
  icon: icons[index],
  year: story.shortPeriod,
}));

const WorksSection = () => {
  const containerRef = useRef(null);
  const trackRef = useRef(null);
  const titleRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      // 1. Horizontal Scroll Setup
      const sections = gsap.utils.toArray(".work-panel");
      // Calculate width of track minus viewport width to know how much to translate
      const trackWidth = trackRef.current.scrollWidth;
      const amountToScroll = trackWidth - window.innerWidth;

      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: containerRef.current,
          start: "top top",
          end: `+=${amountToScroll + 500}`, // Scroll distance roughly matches width
          pin: true,
          scrub: 1,
          snap: {
            snapTo: 1 / (sections.length - 1),
            duration: { min: 0.2, max: 0.5 },
            delay: 0.1,
            ease: "power1.inOut"
          }
        }
      });

      tl.to(trackRef.current, {
        x: -amountToScroll,
        ease: "none"
      });

      // 2. Parallax Images inside panels
      sections.forEach((section) => {
        const img = section.querySelector(".work-image");
        if (img) {
          gsap.fromTo(img, 
            { scale: 1.2, xPercent: -10 },
            { 
              scale: 1, 
              xPercent: 10, 
              ease: "none",
              scrollTrigger: {
                trigger: section,
                containerAnimation: tl,
                start: "left center",
                end: "right center",
                scrub: true,
              }
            }
          );
        }
      });

      // 3. Title Animation (Intro)
      gsap.from(titleRef.current, {
        scrollTrigger: {
          trigger: containerRef.current,
          start: "top 60%",
          toggleActions: "play none none reverse"
        },
        y: 100,
        opacity: 0,
        duration: 1,
        ease: "power3.out"
      });

    }, containerRef);

    return () => ctx.revert();
  }, []);

  return (
    <div id="works" ref={containerRef} className="relative h-screen bg-[#050505] text-white overflow-hidden flex flex-col">
      
      {/* Background Ambience */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(30,30,30,0.4),rgba(5,5,5,1))] pointer-events-none z-0"></div>
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-gray-800 to-transparent opacity-50 z-10"></div>

      {/* Header (Absolute) */}
      <div className="absolute top-8 md:top-12 left-8 md:left-12 z-20 mix-blend-difference">
        <h2 ref={titleRef} className="text-4xl md:text-6xl font-black tracking-tighter uppercase leading-none">
          Career<br/>Impact
        </h2>
      </div>
      
      <div className="absolute bottom-8 right-8 z-20 hidden md:block">
        <div className="text-xs font-mono text-gray-500 uppercase tracking-widest">
           Scroll to Explore <span className="animate-pulse">→</span>
        </div>
      </div>

      {/* Horizontal Track */}
      <div className="flex-1 flex items-center h-full z-10">
        <div ref={trackRef} className="flex h-[80vh] md:h-[70vh] gap-[5vw] px-[5vw] items-center">
          
          {/* Intro Panel (Empty spacer for title visibility) */}
          <div className="work-panel w-[20vw] h-full flex items-end pb-12 shrink-0">
             <div className="w-full border-b border-gray-800 pb-4">
                <div className="text-xs font-mono text-gray-400 mb-2">/ 00</div>
                <div className="text-sm font-bold text-gray-500 uppercase tracking-wider max-w-[200px]">
                   A career built around people, performance, and measurable outcomes.
                </div>
             </div>
          </div>

          {/* Project Cards */}
          {projects.map((project, index) => (
            <div key={project.id} className="work-panel relative w-[85vw] md:w-[60vw] h-full shrink-0 flex flex-col md:flex-row gap-8 group">
              
              {/* Image Container */}
              <div className="relative w-full md:w-[65%] h-[50vh] md:h-full overflow-hidden bg-gray-900 border border-gray-800 shadow-2xl transition-all duration-500 group-hover:border-gray-600">
                <div className="absolute inset-0 bg-black/20 group-hover:bg-transparent transition-colors duration-500 z-10"></div>
                <img 
                  src={project.image} 
                  alt={project.title} 
                  className="work-image w-full h-full object-cover object-top opacity-90 group-hover:opacity-100 transition-opacity duration-500"
                />
                
                {/* Floating Badge */}
                <div className="absolute top-4 right-4 z-20 bg-black/50 backdrop-blur-md border border-white/10 px-3 py-1 rounded-full flex items-center gap-2">
                   <SafeIcon icon={project.icon} className="text-xs text-white" />
                   <span className="text-[10px] font-bold uppercase tracking-widest">{project.year}</span>
                </div>
              </div>

              {/* Info Container */}
              <div className="flex-1 flex flex-col justify-end md:justify-center pb-4 md:pb-0 relative">
                 {/* Large Index Number background */}
                 <div className="absolute -top-10 -left-10 text-[10rem] font-black text-white/5 select-none pointer-events-none z-0">
                    0{index + 1}
                 </div>

                 <div className="relative z-10 space-y-6">
                    <div>
                       <div className="text-xs font-bold text-blue-400 mb-2 uppercase tracking-widest flex items-center gap-2">
                          <span className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></span>
                          {project.subtitle}
                       </div>
                       <h3 className="text-4xl md:text-5xl font-bold leading-tight uppercase tracking-tight mb-4">
                          {project.title}
                       </h3>
                       <p className="text-sm md:text-base text-gray-400 leading-relaxed max-w-sm">
                          {project.description}
                       </p>
                    </div>

                    {/* Tags */}
                    <div className="flex flex-wrap gap-2">
                       {project.tags.map(tag => (
                          <span key={tag} className="px-3 py-1 border border-gray-700 text-[10px] uppercase tracking-wider text-gray-300 hover:bg-white hover:text-black transition-colors cursor-default">
                             {tag}
                          </span>
                       ))}
                    </div>

                    <div className="grid grid-cols-2 border-l border-white/20">
                       {project.evidence.map((item) => (
                          <div key={`${item.value}-${item.label}`} className="border-r border-y border-white/20 p-3">
                             <div className="text-lg font-black text-[#e2c96e]">{item.value}</div>
                             <div className="mt-1 text-[9px] uppercase tracking-wider text-gray-400 leading-tight">{item.label}</div>
                          </div>
                       ))}
                    </div>

                    {/* Dedicated, indexable impact page */}
                    <div className="pt-4">
                       <a href={`/career/${project.slug}/`} aria-label={`Explore Katherine William-Botrous's impact at ${project.title}`} className="inline-flex items-center gap-2 text-sm font-bold border-b border-white pb-1 hover:text-gray-300 hover:border-gray-300 transition-all group/btn">
                          Explore the Impact 
                          <SafeIcon icon={FiArrowUpRight} className="group-hover/btn:translate-x-1 group-hover/btn:-translate-y-1 transition-transform" />
                       </a>
                    </div>
                 </div>
              </div>

            </div>
          ))}

          {/* End Spacer */}
          <div className="work-panel w-[10vw] shrink-0"></div>

        </div>
      </div>
    </div>
  );
};

export default WorksSection;